import { FunctionComponent, useEffect, useState } from 'react'
import Row from '../../../../components/Row'
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import SvgFragment from '../../../../fragments/SvgFragment';
import Text from '../../../../components/Text';
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers';
import { selectConnectionsFilter, useConnectionsStore } from '../../../../stores/connections/useConnectionsStore'
import { useAuthStore } from '../../../../stores/auth/useAuthStore';

interface IQuickActionsProps {
    handleTagFilter: (autotagsFilter: any, isAutotagsFilterOr: 'yes' | 'no', key: string) => void;
}

const QuickActions: FunctionComponent<IQuickActionsProps> = ({ handleTagFilter }) => {
    const { store: { user } } = useAuthStore()
    const { store } = useConnectionsStore()
    const connectionsFilter = selectConnectionsFilter(store)

    const { setScopedConnectionsFilterHandler: setConnectionsFilterHandler } = useConnectionsHandlers()

    const [hoveredTag, setHoveredTag] = useState<string | null>(null)

    const linkedin = {
        "_id": "LinkedIn 1st",
        "title": "LinkedIn 1st",
        "color": "#5964ff",
        "isForbidden": true
    }

    const newConnection = {
        "_id": "New Connection",
        "title": "New Connection",
        "color": "#5964ff",
        "isForbidden": true
    }

    const imported = {
        "_id": "Imported",
        "title": "Imported",
        "color": "#5964ff",
        "isForbidden": true,
    }

    const prospect = {
        "_id": "Prospect",
        "title": "Prospect",
        "color": "#5964ff",
        "isForbidden": true,
    }

    const tags = [
        {
            key: 'linkedin-1st',
            title: 'LinkedIn 1st',
            SVGtype: 'linkedin',
            onClick: () => handleTagFilter([linkedin, newConnection], 'yes', 'linkedin-1st'),
        },
        {
            key: 'linkedin-1st-7days',
            title: 'LinkedIn 1st added last 7 days',
            SVGtype: 'linkedin',
            onClick: () => handleTagFilter([newConnection], 'no', 'linkedin-1st-7days'),
        },
        {
            key: 'linkedin-sidebar',
            title: 'LinkedIn Sidebar',
            SVGtype: 'linkedin',
            onClick: () => handleTagFilter([imported], 'no', 'linkedin-sidebar'),
        },
        {
            key: 'leadsearch',
            title: 'LeadSearch contacts',
            SVGtype: 'sync',
            onClick: () => handleTagFilter([prospect], 'no', 'leadsearch'),
        },
        {
            key: 'job-changes',
            title: 'Job Changes',
            SVGtype: 'briefcase',
            onClick: () => handleJobChangesFilter(),
        },
        {
            key: 'company-changes',
            title: 'Company Changes',
            SVGtype: 'building',
            onClick: () => handleCompanyChangesFilter(),
        },
    ];

    const handleTagClick = (key: string) => {
        tags.find(tag => tag.key === key)?.onClick()
    }

    const handleJobChangesFilter = () => {
        const params = {
            ...connectionsFilter,
            hasJobChanges: connectionsFilter?.hasJobChanges === 'yes' ? undefined : 'yes',
        }
        setConnectionsFilterHandler(params)
    }

    const handleCompanyChangesFilter = () => {
        const params = {
            ...connectionsFilter,
            hasCompanyChanges: connectionsFilter?.hasCompanyChanges === 'yes' ? undefined : 'yes',
        }
        setConnectionsFilterHandler(params)
    }

    const handleResetTagClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        setHoveredTag(null)
        const params = {
            ...connectionsFilter,
            selectedUsersMutual: [],
            autotagsFilter: undefined,
            isAutotagsFilterOr: undefined,
            usersMutualCondition: 'And',
            quickActions: ''
        }
        setConnectionsFilterHandler(params)
    }

    const handleResetJobChangesClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        setHoveredTag(null)
        const params = {
            ...connectionsFilter,
            hasJobChanges: undefined,
        }
        setConnectionsFilterHandler(params)
    }

    const handleResetCompanyChangesClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        setHoveredTag(null)
        const params = {
            ...connectionsFilter,
            hasCompanyChanges: undefined,
        }
        setConnectionsFilterHandler(params)
    }

    const quickActionsToRequiredIds: Record<string, string[]> = {
        'linkedin-1st': ['LinkedIn 1st', 'New Connection'],
        'linkedin-1st-7days': ['New Connection'],
        'linkedin-sidebar': ['Imported'],
        'leadsearch': ['Prospect'],
        'job-changes': [],
        'company-changes': []
    };

    useEffect(() => {
        const userInMutual = connectionsFilter.selectedUsersMutual.some((u: any) => u._id === user._id);

        let missingRequiredTag = false;
        const requiredIds = quickActionsToRequiredIds[connectionsFilter.quickActions] || [];
        if (
            connectionsFilter.quickActions &&
            requiredIds.length > 0 &&
            (
                !connectionsFilter.autotagsFilter ||
                !requiredIds.every(requiredId =>
                    connectionsFilter.autotagsFilter.some((t: any) => t._id === requiredId)
                )
            )
        ) {
            missingRequiredTag = true;
        }

        if (connectionsFilter.quickActions && (!userInMutual || missingRequiredTag)) {
            setConnectionsFilterHandler({
                ...connectionsFilter,
                quickActions: ''
            });
        }
    }, [
        connectionsFilter,
        quickActionsToRequiredIds,
        setConnectionsFilterHandler,
        user._id,
    ]);

    return (
        <StyledRow gap='8px'>
            {tags.map((tag) => {
                let isActive = connectionsFilter?.quickActions === tag.key || hoveredTag === tag.key;
                
                if (tag.key === 'job-changes' && connectionsFilter?.hasJobChanges === 'yes') {
                    isActive = true;
                }
                if (tag.key === 'company-changes' && connectionsFilter?.hasCompanyChanges === 'yes') {
                    isActive = true;
                }

                return (
                    <TagWrapper
                        key={tag.key}
                        onClick={() => handleTagClick(tag.key)}
                        onMouseEnter={() => setHoveredTag(tag.key)}
                        onMouseLeave={() => setHoveredTag(null)}
                        alignItems
                        gap='5px'
                        $active={isActive}
                    >
                        <StyledSvgFragment $active={isActive} type={tag.SVGtype} />
                        <Text $bold $placeholder $active={isActive}>{tag.title}</Text>
                        {((tag.key === 'job-changes' && connectionsFilter?.hasJobChanges === 'yes') || 
                          (tag.key === 'company-changes' && connectionsFilter?.hasCompanyChanges === 'yes') ||
                          (connectionsFilter?.quickActions === tag.key)) && (
                            <div onClick={
                                tag.key === 'job-changes' ? handleResetJobChangesClick :
                                tag.key === 'company-changes' ? handleResetCompanyChangesClick :
                                handleResetTagClick
                            }>
                                <SvgFragment type='closeV2'/>
                            </div>
                        )}
                    </TagWrapper>
                );
            })}
        </StyledRow>
    )
};

const StyledRow = styled(Row)`
    padding: 16px 0 0 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
`
const TagWrapper = styled(Row)<{ $active?: boolean }>`
    cursor: pointer;
    padding: 0 5px;
    background-color: ${styles.colors.white};
    border-radius: 4px;
    border: 1px solid ${({$active}) => $active ? styles.colors.primary600 : 'transparent'};
    transition: background 0.2s, border 0.2s;
    flex: 0 0 auto;
    flex-wrap: nowrap;
    white-space: nowrap;
`
const StyledSvgFragment = styled(SvgFragment)<{ $active?: boolean }>`
    fill: ${({$active}) => $active ? styles.colors.primary600 : styles.colors.black300};
    & path { fill: ${({$active}) => $active ? styles.colors.primary600 : styles.colors.black300} };
`
export default QuickActions